import { Component, DestroyRef, inject, Inject, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { TranslatorService } from '../../services/translator_service';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ResponsiveService } from '../../services/responsive.service';
import { ApiQuery } from '../../web-services/api/api.query';
import { CommonModule } from '@angular/common';
import { RTLDivDirectiveDirective } from '../../directives/rtldiv-directive.directive';
import { TranslateModule } from '@ngx-translate/core';
import { MatTabsModule, MatTabGroup } from '@angular/material/tabs';
import moment from 'moment';
import { UtilService } from '../../services/util.service';
import { ManageBinsModalComponent } from '../inventory/manage-bins-modal/manage-bins-modal.component';
import { ManageBinModalComponent } from '../inventory/manage-bin-modal/manage-bin-modal.component';
import { BinMetricsComponent } from './bin-metrics/bin-metrics.component';
import { BinLocationComponent } from './bin-location/bin-location.component';
import { BinEventsComponent } from './bin-events/bin-events.component';
import { BinCapacityComponent } from './bin-capacity/bin-capacity.component';
import { BinCollectionComponent } from './bin-collection/bin-collection.component';
import { BinTicketsComponent } from './bin-tickets/bin-tickets.component';
import { ApiService } from '../../web-services/api/api.service';
import { InventoryBinsComponent } from './inventory-bins/inventory-bins.component';
import { ApiStore } from '../../web-services/api/api.store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-bin-live-info-modal',
  standalone: true,
  imports: [TranslateModule,RTLDivDirectiveDirective,CommonModule,MatTabsModule,BinMetricsComponent,
	BinLocationComponent,BinEventsComponent,BinCapacityComponent,BinCollectionComponent,BinTicketsComponent],
  templateUrl: './bin-live-info-modal.component.html',
  styleUrl: './bin-live-info-modal.component.scss'
})
export class BinLiveInfoModalComponent implements AfterViewInit, OnDestroy {
  static openModalRef: any = null;
  @ViewChild('matTabGroupBins') matTabGroupBins: MatTabGroup;
  translationsObj;
  currentLang;
  isMobile: boolean;
  usageType = 1;
  tabs = [];
  selectedBin:any;
  displayedFields = {isAll : true};
  selectedTabIndex = 0;
  selectedBinInfo:any;
  isProgrammaticChange = false;
  isMinimized = false;
  tabContentItemIndex = 0;
  private intersectionObserver: IntersectionObserver;
  private readonly destroy$ = new Subject<void>();
  private isClosing = false;
  user_role_id = 0;

  constructor(private dialogRef: MatDialogRef<BinLiveInfoModalComponent>,private translator: TranslatorService,
    private responsiveService: ResponsiveService,@Inject(MAT_DIALOG_DATA) public data: any,private apiQuery:ApiQuery,
    private utilService: UtilService,private dialog: MatDialog,private apiService:ApiService,private apiStore:ApiStore) {
      this.dialogRef.disableClose = true;
      this.translator.currentLangEmitter$
	  .pipe(takeUntil(this.destroy$))
      .subscribe(async value=>{						
        this.translationsObj = await this.translator.getTranslation(value).toPromise()			 
        this.currentLang = value;	
    }); 
  }

  ngOnInit() {
    this.onResize();
    this.responsiveService.checkWidth();
    this.apiQuery.user$.pipe(takeUntil(this.destroy$)).subscribe(user => {
      if (!user) {
        return;
      }
      this.usageType = user["usage_type_id"];
	  this.user_role_id = user["user_role_id"];
    });
    this.tabs = this.data.item;
    this.binTabChanged(0);

	this.apiQuery.binInfo$.pipe(takeUntil(this.destroy$)).subscribe((info) => {
		this.selectedBinInfo = info;
	});
  }

  ngAfterViewInit() {
	this.setupScrollObserver();
  }

  ngOnDestroy() {
	this.isClosing = true;
	this.destroy$.next();
	this.destroy$.complete();
	if (this.intersectionObserver) {
		this.intersectionObserver.disconnect();
	}
	this.tabs = [];
	this.selectedBin = null;
	this.selectedBinInfo = null;
	// Clear static reference when modal is destroyed
	if (BinLiveInfoModalComponent.openModalRef && BinLiveInfoModalComponent.openModalRef.componentInstance === this) {
		BinLiveInfoModalComponent.openModalRef = null;
	}
  }

  copyToClipboard(text: string) {
	this.utilService.copyToClipboard(text);
  }

  setupScrollObserver() {
	// Disconnect existing observer if any
	if (this.intersectionObserver) {
		this.intersectionObserver.disconnect();
	}

	// Find the scroll container in the currently active tab
	const activeTabContent = document.querySelector('.mat-mdc-tab-body-active .tab-content-body-tabs') as HTMLElement;
	if (!activeTabContent) {
		// Retry once if active tab not found (DOM might not be ready yet)
		requestAnimationFrame(() => {
			const retryActiveTabContent = document.querySelector('.mat-mdc-tab-body-active .tab-content-body-tabs') as HTMLElement;
			if (retryActiveTabContent) {
				this.initializeObserver(retryActiveTabContent);
			}
		});
		return;
	}

	this.initializeObserver(activeTabContent);
  }

  private initializeObserver(activeTabContent: HTMLElement) {
	const options = {
		root: activeTabContent,
		rootMargin: '-20% 0px -60% 0px',
		threshold: 0
	};

	this.intersectionObserver = new IntersectionObserver((entries) => {
		entries.forEach(entry => {
			if (entry.isIntersecting) {
				const elementId = entry.target.id;
				const match = elementId.match(/bin-content-(\d+)/);
				if (match) {
					const index = parseInt(match[1], 10);
					this.tabContentItemIndex = index;
				}
			}
		});
	}, options);

	// Observe elements within the active tab only
	for (let i = 0; i < 6; i++) {
		const element = activeTabContent.querySelector(`#bin-content-${i}`) as HTMLElement;
		if (element) {
			this.intersectionObserver.observe(element);
		}
	}
  }

  onResize() {
		this.responsiveService.getMobileStatus().pipe(takeUntil(this.destroy$)).subscribe(isMobile => {
		  this.isMobile = isMobile;
		});
	}
  
  closeModal(){
	this.isClosing = true;
	BinLiveInfoModalComponent.openModalRef = null;
	this.dialogRef.close();
  }

  minimizeModal(){
	this.isMinimized = true;
	this.dialogRef.disableClose = false;
	const dialogContainer = document.querySelector('.cdk-overlay-pane');
	const backdrop = document.querySelector('.cdk-overlay-backdrop');
	if (dialogContainer) {
		this.currentLang != 'en' ? dialogContainer.classList.add('minimized-modal-rtl') : dialogContainer.classList.add('minimized-modal-ltr');
	}
	if (backdrop) {
		backdrop.classList.add('minimized-backdrop');
	}
  }

  maximizeModal(){
	this.isMinimized = false;
	this.dialogRef.disableClose = true;
	const dialogContainer = document.querySelector('.cdk-overlay-pane');
	const backdrop = document.querySelector('.cdk-overlay-backdrop');
	if (dialogContainer) {
		this.currentLang != 'en' ? dialogContainer.classList.remove('minimized-modal-rtl') : dialogContainer.classList.remove('minimized-modal-ltr');
	}
	if (backdrop) {
		backdrop.classList.remove('minimized-backdrop');
	}
	// Ensure bin data is loaded for the selected tab
	// The tab selection is handled by the [selectedIndex] binding in the template
	if (this.selectedTabIndex >= 0 && this.selectedTabIndex < this.tabs.length) {
		const restoredTab = this.tabs[this.selectedTabIndex];
		if (restoredTab && (!this.selectedBin || this.selectedBin.bin_id !== restoredTab.bin_id)) {
			this.selectedBin = restoredTab;
			this.apiStore.update({ binInfo: null });
			this.apiService.getBinInfo(this.selectedBin?.bin_id);
			this.apiService.getBinEvents(this.selectedBin?.bin_id);
			this.apiService.getBinCollection(this.selectedBin?.bin_id);
			const end_date = moment().startOf('day').format('YYYY-MM-DD 23:59:59');
			const start_date = moment().startOf('day').subtract(1, 'month').format('YYYY-MM-DD HH:mm:ss');
			this.apiService.binsCapacityData(this.selectedBin?.bin_id, start_date, end_date);
		}
	}
	// Re-setup scroll observer after maximizing the modal
	requestAnimationFrame(() => {
		setTimeout(() => {
			this.setupScrollObserver();
		}, 100);
	});
  }

  getMinimizedTitle(): string {
	if (!this.tabs || this.tabs.length === 0) {
		return '';
	}
	// Use the last viewed bin (selectedTabIndex) instead of the first tab
	const lastViewedTab = this.tabs[this.selectedTabIndex];
	if (lastViewedTab) {
		return lastViewedTab.bin_name || '';
	}
	// Fallback to first tab if selectedTabIndex is invalid
	return this.tabs[0]?.bin_name || '';
  }

  getMinimizedRemainingTabsCount(): string {
	if (!this.tabs || this.tabs.length === 0) {
		return '';
	}
	// Count remaining tabs excluding the currently selected one
	const remainingTabs = this.tabs.length - 1;
	if (remainingTabs > 0) {
		return `+${remainingTabs} ${this.translationsObj.LIVE_INFO.REMAINING_TABS}`;
	}
	return '';
  }

  removeTab(index: number){
	this.tabs.splice(index, 1);
  }

  addTabs(newBins: any[]){
	if(!newBins || newBins.length === 0) {
		return;
	}

	// Maximize the modal if it's minimized
	if(this.isMinimized) {
		this.maximizeModal();
	}

	// Add new bins to tabs, avoiding duplicates, up to maximum of 8 tabs
	const maxTabs = 8;
	newBins.forEach(bin => {
		if(this.tabs.length >= maxTabs) {
			return; // Stop adding if we've reached the maximum
		}
		const existingIndex = this.tabs.findIndex(tab => tab.bin_id === bin.bin_id);
		if(existingIndex === -1) {
			this.tabs.push(bin);
		}
	});

	// Focus the bin the user just clicked (the last one in newBins) instead of
	// leaving the previously-active / first tab selected. We must call
	// binTabChanged() explicitly: changing the [selectedIndex] input alone only
	// moves the tab header, it does NOT re-emit (selectedIndexChange), so the bin
	// data would not reload. binTabChanged() sets selectedTabIndex (switching the
	// header) AND loads the clicked bin's data.
	const clickedBin = newBins[newBins.length - 1];
	const newIndex = this.tabs.findIndex(tab => tab.bin_id === clickedBin.bin_id);
	if(newIndex >= 0) {
		this.binTabChanged(newIndex);
	}
  }

  binTabChanged(index: number){
	if (this.isProgrammaticChange) {
		return; // Exit early if this is a programmatic change
	}
	// Prevent opening compare modal when component is closing
	if (this.isClosing) {
		return;
	}
	if(this.tabs[index]){
		this.selectedTabIndex = index;
	}

    if (index == this.tabs.length) {
      this.compareBins();
      if (this.matTabGroupBins) {
		this.isProgrammaticChange = true;
		this.matTabGroupBins.selectedIndex = this.selectedTabIndex;
		setTimeout(() => {
		  this.isProgrammaticChange = false;
		}, 0);
      }
      return;
    }
    this.selectedBin = this.tabs[index];
	this.apiStore.update({ binInfo: null });
	this.apiService.getBinInfo(this.selectedBin?.bin_id);
    this.apiService.getBinEvents(this.selectedBin?.bin_id);
	this.apiService.getBinCollection(this.selectedBin?.bin_id);

    const end_date = moment().startOf('day').format('YYYY-MM-DD 23:59:59');
    const start_date = moment().startOf('day').subtract(1, 'month').format('YYYY-MM-DD HH:mm:ss');
    this.apiService.binsCapacityData(this.selectedBin?.bin_id, start_date, end_date);
	
	// Re-setup scroll observer for the newly active tab
	requestAnimationFrame(() => {
		this.setupScrollObserver();
	});
  }

  tabChanged(index: number){
	this.tabContentItemIndex = index;
	const tabContent = document.getElementById(`bin-content-${index}`);
	if (tabContent) {
		const scrollContainer = tabContent.closest('.tab-content-body-tabs') as HTMLElement;
		if (scrollContainer) {
			const containerRect = scrollContainer.getBoundingClientRect();
			const targetRect = tabContent.getBoundingClientRect();
			const scrollTop = scrollContainer.scrollTop;
			const targetOffsetTop = targetRect.top - containerRect.top + scrollTop;
			scrollContainer.scrollTo({ top: targetOffsetTop - 24, behavior: 'smooth' });
		}
	}
  }

  compareBins(){
	const dialogRef = this.dialog.open(InventoryBinsComponent, { panelClass: 'bulkBins-container' , data: {chosenBins : this.tabs} });

	dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(result => {
		this.tabs  = result.tabsToCompare;
		// Re-setup scroll observer after tabs are updated
		requestAnimationFrame(() => {
			this.setupScrollObserver();
		});
	});
  }

  editBin(){
	this.dialog.open(ManageBinModalComponent, { panelClass: 'bulkBins-container' , data: {chosenBins : this.selectedBinInfo, properties : this.displayedFields} });
  }

  manageBins(){
	this.dialog.open(ManageBinsModalComponent, { panelClass: 'bulkBins-container' , data: {chosenBins : this.tabs} });
  }

  calcDiff = (date: string) => {
		if (this.translationsObj != undefined) {
			let diff_in_string = '';
			const original_date = moment(date);
			const date_time_now = moment();
			const diff_in_object: any = this.utilService.preciseDiffBetweenDates(original_date, date_time_now, true);
			if (diff_in_object.months > 0) {
				diff_in_string = diff_in_string + diff_in_object.months + ' ';
				if (diff_in_object.months === 1) {
					diff_in_string += this.translationsObj.COLLECTION_REPORT.MONTH + ' ';
				} else {
					diff_in_string += this.translationsObj.COLLECTION_REPORT.MONTHS + ' ';
				}
			}
			if (diff_in_object.days > 0) {
				diff_in_string = diff_in_string + diff_in_object.days + ' ';
				if (diff_in_object.days === 1) {
					diff_in_string += this.translationsObj.COLLECTION_REPORT.DAY + ' ';
				} else {
					diff_in_string += this.translationsObj.COLLECTION_REPORT.DAYS + ' ';
				}
			}
			if (diff_in_object.hours > 0) {
				if (diff_in_object.days > 0) {
					diff_in_string += this.translationsObj.COLLECTION_REPORT.AND + ' ' + diff_in_object.hours + ' ';
				} else {
					diff_in_string += diff_in_object.hours + ' ';
				}
				if (diff_in_object.hours === 1) {
					diff_in_string += this.translationsObj.COLLECTION_REPORT.HOUR + ' ';
				} else {
					diff_in_string += this.translationsObj.COLLECTION_REPORT.HOURS + ' ';
				}
			}
			
			if (diff_in_object.minutes > 0) {
				if (diff_in_object.hours > 0) {
					diff_in_string += this.translationsObj.COLLECTION_REPORT.AND + ' ' + diff_in_object.minutes + ' ';
				} else {
					diff_in_string += diff_in_object.minutes + ' ';
				}
				if (diff_in_object.minutes === 1) {
					diff_in_string += this.translationsObj.COLLECTION_REPORT.MINUTE + ' ';
				} else {
					diff_in_string += this.translationsObj.COLLECTION_REPORT.MINUTES + ' ';
				}
			}
			else {
				diff_in_string += this.translationsObj.COLLECTION_REPORT.MINUTE + ' ';
			}

			if (this.currentLang !== 'iw') {
				diff_in_string += this.translationsObj.COLLECTION_REPORT.AGO;
			}
			else {
				diff_in_string = this.translationsObj.COLLECTION_REPORT.AGO + ' ' + diff_in_string;
			}
			return diff_in_string;
		}	
	}
}
